import { useMemo } from 'react'
import { Check, CircleNotch } from '@/components/icons'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuFilterHeader,
  DropdownMenuFilterTrigger,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'

/**
 * Multi-select directory dropdown backing the Vendor filter above the
 * Purchase Orders table. It shares the checkbox/count/header idiom used by
 * Status, BC and Channel.
 *
 * Rendering rules:
 *   * Loading   — the trigger shows the label + a spinner + "Loading…";
 *                 clicking still opens the menu and Clear remains available.
 *   * Error     — selected values are retained with a tooltip
 *                 in the URL so a manual reload can recover.
 *   * Empty     — "None configured" copy inside the menu. Common on a new
 *                 tenant before backfill has run (see
 *                 vision/docs/BACKFILL_V1.md).
 *   * Populated — rows stay in the order the API returned them (the server
 *                 already sorts by displayName ASC).
 */
export interface ScopeOption {
  code: string
  displayName: string
}

export function MultiScopeFilter({
  label,
  ariaLabel,
  emptyLabel,
  value,
  options,
  loading,
  error,
  onChange,
}: {
  label: string
  ariaLabel: string
  emptyLabel: string
  value: string[]
  options: ScopeOption[]
  loading: boolean
  error: boolean
  onChange: (next: string[]) => void
}) {
  const toggle = (option: string) =>
    onChange(value.includes(option) ? value.filter(entry => entry !== option) : [...value, option])

  return (
    <DropdownMenu>
      <DropdownMenuFilterTrigger
        label={label}
        loading={loading}
        aria-label={ariaLabel}
        title={error ? "Couldn't load the list. Showing the last selection." : undefined}
      >
        {value.length > 0 ? (
          <Badge tone="info" className="mr-1.5 tabular-nums">
            {value.length}
          </Badge>
        ) : (
          <span className="mr-1.5 text-[color:var(--foreground)]">Any</span>
        )}
      </DropdownMenuFilterTrigger>
      <DropdownMenuContent align="start" className="min-w-56">
        <DropdownMenuFilterHeader onClear={value.length > 0 ? () => onChange([]) : undefined}>
          Filter by {label.toLowerCase()}
        </DropdownMenuFilterHeader>
        <DropdownMenuSeparator />
        {options.length === 0 && !loading && (
          <div className="px-2 py-1.5 text-[11.5px] text-[color:var(--muted-foreground)]">
            {emptyLabel}
          </div>
        )}
        {loading && options.length === 0 && (
          <div className="flex items-center gap-2 px-2 py-1.5 text-[11.5px] text-[color:var(--muted-foreground)]">
            <CircleNotch className="size-3 animate-spin" /> Loading…
          </div>
        )}
        {options.map(option => (
          <DropdownMenuCheckboxItem
            key={option.code}
            checked={value.includes(option.code)}
            onCheckedChange={() => toggle(option.code)}
            onSelect={event => event.preventDefault()}
          >
            <span className="truncate">{option.displayName}</span>
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Single-select variant retained for workflows whose APIs accept one value. */
export function ScopeFilter({
  label,
  ariaLabel,
  emptyLabel,
  value,
  options,
  loading,
  error,
  onChange,
}: {
  label: string
  ariaLabel: string
  emptyLabel: string
  value: string | null
  options: ScopeOption[]
  loading: boolean
  error: boolean
  onChange: (next: string | null) => void
}) {
  const selected = useMemo(() => options.find(option => option.code === value), [options, value])
  const currentLabel = selected?.displayName ?? (value && !loading ? value : 'All')

  return (
    <DropdownMenu>
      <DropdownMenuFilterTrigger
        label={label}
        loading={loading}
        aria-label={ariaLabel}
        title={error ? "Couldn't load the list. Showing the last selection." : undefined}
      >
        <span className="mr-1.5 max-w-[10rem] truncate text-[color:var(--foreground)]">
          {currentLabel}
        </span>
      </DropdownMenuFilterTrigger>
      <DropdownMenuContent align="start" className="min-w-56">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="justify-between" onSelect={() => onChange(null)}>
          <span>All</span>
          {value == null && <Check className="size-3.5 text-[color:var(--primary)]" />}
        </DropdownMenuItem>
        {options.length > 0 && <DropdownMenuSeparator />}
        {options.length === 0 && !loading && (
          <div className="px-2 py-1.5 text-[11.5px] text-[color:var(--muted-foreground)]">
            {emptyLabel}
          </div>
        )}
        {loading && options.length === 0 && (
          <div className="flex items-center gap-2 px-2 py-1.5 text-[11.5px] text-[color:var(--muted-foreground)]">
            <CircleNotch className="size-3 animate-spin" /> Loading…
          </div>
        )}
        {options.map(option => (
          <DropdownMenuItem
            key={option.code}
            className="justify-between"
            onSelect={() => onChange(option.code)}
          >
            <span className="truncate">{option.displayName}</span>
            {option.code === value && <Check className="size-3.5 text-[color:var(--primary)]" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
